// SignIn.js
import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate } from "react-router-dom";
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { LinkedIn } from 'react-linkedin-login-oauth2';
import { jwtDecode } from "jwt-decode";
import backendURL, { google_login_api, linkedin_client_id, linkedin_redirect_uri, SETUP_COMPLETE_LOCAL_KEY } from "../../config";
import brandingBg from '../../assets/images/bot-avatar.jpg';
import SchoolIcon from '@mui/icons-material/School';
import {
  Box, Typography, TextField, Button, Alert, Divider, Link as MuiLink, useTheme, CircularProgress,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LanguageIcon from '@mui/icons-material/Language';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';

// Google icon SVG
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

// LinkedIn icon SVG
const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

// KlonIT Logo SVG
const KlonITLogo = ({ color = "white", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
    <path d="M8 6L16 16L8 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M16 6L24 16L16 26" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.6" />
  </svg>
);

const colors = {
  sidebarBg: '#1e293b',
  sidebarHover: '#313d4a',
  sidebarText: '#dee4ee',
  sidebarTextMuted: '#8a99af',
  accent: '#3b82f6',
  bgPrimary: '#f8fafc',
  bgMuted: '#f1f5f9',
  border: '#e2e8f0',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  success: '#059669',
  danger: '#dc2626',
};

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const socialButtonSx = {
  py: 1.25, borderRadius: '14px', fontFamily, textTransform: 'none', fontWeight: 500,
  fontSize: '0.875rem', color: '#1e293b', borderColor: 'rgba(0,0,0,0.12)', bgcolor: 'rgba(255,255,255,0.85)',
  boxShadow: 'none', height: 44, backdropFilter: 'blur(8px)',
  '&:hover': { bgcolor: 'rgba(255,255,255,1)', borderColor: 'rgba(0,0,0,0.2)', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  '&:disabled': { opacity: 0.6 },
};

const FeatureCard = ({ icon, title, description }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 2,
    p: '1rem 1.25rem', bgcolor: colors.sidebarHover,
    border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px',
    transition: 'all 0.25s ease',
    '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.2)', transform: 'translateX(4px)' },
  }}>
    <Box sx={{
      width: 42, height: 42, bgcolor: 'rgba(59,130,246,0.15)', borderRadius: '10px',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: colors.accent,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: colors.sidebarText, mb: 0.25 }}>{title}</Typography>
      <Typography sx={{ fontSize: '0.875rem', color: colors.sidebarTextMuted, lineHeight: 1.5 }}>{description}</Typography>
    </Box>
  </Box>
);

function SignInInner() {
  const theme = useTheme();

  useEffect(() => {
    document.title = 'Login | KlonIT Web Application';
  }, []);

  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState(null); // 'google' | 'linkedin' | null
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [showVerificationRequired, setShowVerificationRequired] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(null);
  const navigate = useNavigate();

  const anyLoading = isLoading || !!socialLoading || resendLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.post(`/login`, { email, password });
      const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
      if (responseData) {
        localStorage.removeItem('my_access_token');
        localStorage.removeItem('my_user');
        localStorage.setItem('user', JSON.stringify(responseData.user));
        localStorage.setItem('customer_portal_token', responseData.customer_portal_token);
        console.log("Signed In...")
        if (!responseData.user.profile || responseData.user.active_team.has_pending_terms || !responseData.user.active_team.subscription) {
          localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
          window.location.href = "/trading-portal/dashboard";
        } else {
          // Check user role for panel-based navigation
          const userRole = responseData.user.active_team?.user_role_in_team?.toLowerCase();

          if (userRole === 'student') {
            localStorage.setItem('active_panel', 'trading');
            window.location.href = "/trading-portal/dashboard";
          } else if (userRole === 'customer') {
            localStorage.setItem('active_panel', 'trading');
            window.location.href = "/trading-portal/dashboard";
          } else {
            // Admin/Employee users go to Creator Panel
            localStorage.setItem('active_panel', 'trading');
            window.location.href = "/trading-portal/dashboard";
          }
        }
      } else {
        throw new Error(response.data.message || "Login failed.");
      }
    } catch (error) {
      if (error.response) {
        // Check if email verification is required
        if (error.response.data.code === 'EMAIL_NOT_VERIFIED') {
          setShowVerificationRequired(true);
          setError(null);
        } else {
          setError(error.response.data.message);
          setShowVerificationRequired(false);
        }
      } else if (error.request) {
        setError("No response received from API.");
      } else {
        setError("Error setting up request");
      }
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError("Please enter your email address first.");
      return;
    }
    setResendLoading(true);
    setResendSuccess(null);
    setError(null);
    try {
      const response = await axiosInstance.post(`/resend-verification`, { email });
      setResendSuccess(response.data.message || "Verification email sent! Please check your inbox.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend verification email.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleLinkedInLoginSuccess = async (response) => {
    setError(null);
    setSocialLoading('linkedin');
    const authCode = response;
    if (!authCode) {
      setError("LinkedIn authentication code not received.");
      setSocialLoading(null);
      return;
    }
    let payloadObject = { auth_code: authCode, redirect_uri: linkedin_redirect_uri }
    let linkedInUser = null;
    try {
      const tokenResponse = await axiosInstance.post(`/api/linkedin/oauth`, payloadObject, { headers: { 'Content-Type': 'application/json' } });
      const profileResponseData = typeof tokenResponse.data.data === 'string' ? JSON.parse(tokenResponse.data.data) : tokenResponse.data.data;
      linkedInUser = profileResponseData?.user;
      const payload = { email: linkedInUser.email, social_id: linkedInUser.sub, provider: 'linkedin' }
      const socialResponse = await axiosInstance.post(`/social-login`, payload);
      const socialResponseData = typeof socialResponse.data.data === 'string' ? JSON.parse(socialResponse.data.data) : socialResponse.data.data;
      if (socialResponseData) {
        localStorage.removeItem('my_user');
        localStorage.setItem('user', JSON.stringify(socialResponseData.user));
        localStorage.setItem('customer_portal_token', socialResponseData.customer_portal_token);
        console.log("Signed In...")
        if (!socialResponseData.user.profile || socialResponseData.user.active_team.has_pending_terms || !socialResponseData.user.active_team.subscription) {
          localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
          window.location.href = "/trading-portal/dashboard";
        } else {
          const userRole = socialResponseData.user.active_team?.user_role_in_team?.toLowerCase();

          if (userRole === 'student') {
            localStorage.setItem('active_panel', 'trading');
            window.location.href = "/trading-portal/dashboard";
          } else if (userRole === 'customer') {
            localStorage.setItem('active_panel', 'trading');
            window.location.href = "/trading-portal/dashboard";
          } else {
            localStorage.setItem('active_panel', 'trading');
            window.location.href = "/trading-portal/dashboard";
          }
        }
      } else {
        throw new Error(response.data.message || "Login failed.");
      }
    } catch (error) {
      if (error?.response?.data?.code === 'ACCOUNT_NOT_FOUND') {
        setError("Account not found. Please sign up first.");
        setSocialLoading(null);
      } else {
        setError(error?.response?.data?.message || "No username or password found. Please check you login or sign-up");
        setSocialLoading(null);
      }
    }
  };

  const handleLinkedInLoginFailure = (error) => {
    if (error?.error != "user_closed_popup") {
      console.error("LinkedIn signup failure:", error);
      setError("LinkedIn signup failed.");
    }
    setSocialLoading(null);
  };

  const handleGoogleLoginFailure = () => {
    setError("No username or password found. Please check you login or sign-up");
    setSocialLoading(null);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setSocialLoading('google');
      setError(null);
      let userInfo = null;
      try {
        const res = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });
        userInfo = await res.json();
        const payload = { email: userInfo.email, social_id: userInfo.sub, provider: 'google' };
        const socialResponse = await axiosInstance.post(`/social-login`, payload);
        const socialResponseData = typeof socialResponse.data.data === 'string' ? JSON.parse(socialResponse.data.data) : socialResponse.data.data;
        if (socialResponseData) {
          localStorage.removeItem('my_user');
          localStorage.setItem('user', JSON.stringify(socialResponseData.user));
          localStorage.setItem('customer_portal_token', socialResponseData.customer_portal_token);
          console.log("Signed In...")
          if (!socialResponseData.user.profile || socialResponseData.user.active_team.has_pending_terms || !socialResponseData.user.active_team.subscription) {
            localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
            window.location.href = "/trading-portal/dashboard";
          } else {
            const userRole = socialResponseData.user.active_team?.user_role_in_team?.toLowerCase();

            if (userRole === 'student') {
              localStorage.setItem('active_panel', 'trading');
              window.location.href = "/trading-portal/dashboard";
            } else if (userRole === 'customer') {
              localStorage.setItem('active_panel', 'trading');
              window.location.href = "/trading-portal/dashboard";
            } else {
              localStorage.setItem('active_panel', 'trading');
              window.location.href = "/trading-portal/dashboard";
            }
          }
        } else {
          throw new Error("Login failed.");
        }
      } catch (error) {
        if (error?.response?.data?.code === 'ACCOUNT_NOT_FOUND') {
          setError("Account not found. Please sign up first.");
          setSocialLoading(null);
        } else {
          setError(error?.response?.data?.message || "No username or password found. Please check you login or sign-up");
          setSocialLoading(null);
        }
      }
    },
    onError: handleGoogleLoginFailure,
  });

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px', fontFamily, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(8px)',
      border: 'none', outline: 'none',
      '& .MuiOutlinedInput-notchedOutline': { display: 'none !important' },
      '& fieldset': { display: 'none !important' },
      '&.Mui-focused': { bgcolor: 'rgba(255,255,255,1)', boxShadow: `0 0 0 2px ${theme.palette.primary.main}40` },
      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
    },
    '& .MuiInputBase-input': {
      fontFamily, fontSize: '0.875rem', py: '0.75rem', px: '0.5rem',
      border: 'none', outline: 'none',
      '&:focus': { outline: 'none', boxShadow: 'none' },
      '&::placeholder': { color: colors.textTertiary, opacity: 1 },
    },
    '& .MuiInputAdornment-root': { color: colors.textTertiary },
    '& .MuiInputLabel-root': { fontFamily },
    '& .MuiTextField-root': { border: 'none', outline: 'none' },
  };

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,200..800;1,200..800&display=swap');`}</style>

      <Box sx={{ fontFamily, minHeight: '100vh', bgcolor: colors.bgPrimary, display: 'flex' }}>
        {/* Left Branding Panel */}
        <Box sx={{
          display: { xs: 'none', lg: 'flex' }, width: '50%',
          flexDirection: 'column', justifyContent: 'center', p: '4rem', position: 'relative', overflow: 'hidden',
          backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(15, 23, 42, 0.9)), url(${brandingBg})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          '&::before': {
            content: '""', position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.12) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(59,130,246,0.08) 0%, transparent 40%)',
          },
        }}>
          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 480, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.875rem', mb: 3 }}>
              <Box sx={{
                width: 48, height: 48, bgcolor: colors.accent, borderRadius: '12px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
              }}>
                <KlonITLogo />
              </Box>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.025em', fontFamily }}>KlonIT</Typography>
            </Box>

            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 2, py: 0.5, bgcolor: colors.sidebarHover,
              border: '1px solid rgba(255,255,255,0.08)', borderRadius: '100px',
              fontSize: '0.8125rem', fontWeight: 600, color: colors.accent, mb: 2, letterSpacing: '0.05em',
            }}>
              <BoltIcon sx={{ fontSize: 14 }} />
              CLONE · SCALE · GROW
            </Box>

            <Typography sx={{ fontSize: '2.75rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.03em', color: '#fff', mb: 1.5, fontFamily }}>
              AI-Powered<br /><Box component="span" sx={{ color: colors.accent }}>Business Growth</Box>
            </Typography>

            <Typography sx={{ fontSize: '1rem', lineHeight: 1.7, color: colors.sidebarTextMuted, mb: 2.5, fontFamily }}>
              Deploy intelligent AI agents that engage customers 24/7, automate workflows, and scale your business across 52+ languages.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
              <FeatureCard icon={<ChatBubbleOutlineIcon sx={{ fontSize: 20 }} />} title="24/7 AI Agents" description="Engage customers on web, WhatsApp & email" />
              <FeatureCard icon={<LanguageIcon sx={{ fontSize: 20 }} />} title="52+ Languages" description="Scale globally with native language support" />
              <FeatureCard
                icon={<SchoolIcon sx={{ fontSize: 20 }} />}
                title="Student Learning"
                description="Interactive lessons, tutoring & progress tracking"
              />
            </Box>
          </Box>
        </Box>

        {/* Right Form Panel */}
        <Box sx={{
          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: { xs: 2, lg: '3rem 4rem' }, position: 'relative', overflow: 'hidden',
          background: `linear-gradient(135deg, #f8fafc 0%, #eef2ff 40%, #e0e7ff 70%, #f0f4ff 100%)`,
          '&::before': {
            content: '""', position: 'absolute', top: '-30%', right: '-20%',
            width: '500px', height: '500px', borderRadius: '50%', pointerEvents: 'none',
            background: `radial-gradient(circle, ${theme.palette.primary.main}12 0%, transparent 70%)`,
          },
          '&::after': {
            content: '""', position: 'absolute', bottom: '-20%', left: '-10%',
            width: '400px', height: '400px', borderRadius: '50%', pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(139,92,246,0.08) 0%, transparent 70%)',
          },
        }}>
          <Box sx={{ width: '100%', maxWidth: 420, position: 'relative', zIndex: 1 }}>
            {/* Mobile Logo */}
            <Box sx={{ display: { xs: 'flex', lg: 'none' }, alignItems: 'center', justifyContent: 'center', gap: '0.75rem', mb: 2 }}>
              <Box sx={{ width: 44, height: 44, bgcolor: colors.sidebarBg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <KlonITLogo color="white" />
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.025em', fontFamily }}>KlonIT</Typography>
            </Box>

            {/* Header with icon */}
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Box sx={{
                width: 48, height: 48, borderRadius: '14px', mx: 'auto', mb: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
              }}>
                <LockOpenOutlinedIcon sx={{ color: '#fff', fontSize: 24 }} />
              </Box>
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: colors.textPrimary, letterSpacing: '-0.02em', mb: 0.25, fontFamily }}>
                Welcome Back
              </Typography>
              <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
                Sign in to your KlonIT account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 1.5, borderRadius: '12px', fontFamily, fontSize: '0.8125rem', '& .MuiAlert-message': { fontFamily } }}>
                {error}
              </Alert>
            )}

            {resendSuccess && (
              <Alert severity="success" sx={{ mb: 1.5, borderRadius: '12px', fontFamily, fontSize: '0.8125rem', '& .MuiAlert-message': { fontFamily } }}>
                {resendSuccess}
              </Alert>
            )}

            {/* Email Verification Required */}
            {showVerificationRequired && (
              <Box sx={{
                mb: 2, p: 2.5, borderRadius: '16px', bgcolor: 'rgba(251, 191, 36, 0.1)',
                border: '1px solid rgba(251, 191, 36, 0.3)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '10px',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MarkEmailReadOutlinedIcon sx={{ color: '#fff', fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: colors.textPrimary, fontFamily }}>
                      Email Verification Required
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: colors.textSecondary, fontFamily }}>
                      Please check your inbox and verify your email
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.8125rem', color: colors.textSecondary, mb: 2, fontFamily }}>
                  We've sent a verification link to <strong>{email}</strong>. Click the link in the email to activate your account.
                </Typography>
                <Button
                  onClick={handleResendVerification}
                  fullWidth disabled={resendLoading}
                  startIcon={resendLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <RefreshIcon />}
                  sx={{
                    py: '0.625rem', fontFamily, fontSize: '0.8125rem', fontWeight: 600, textTransform: 'none',
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: '#fff', borderRadius: '12px',
                    '&:hover': { background: 'linear-gradient(135deg, #d97706, #f59e0b)' },
                    '&:disabled': { opacity: 0.7 },
                  }}
                >
                  {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                </Button>
                <Button
                  onClick={() => { setShowVerificationRequired(false); setResendSuccess(null); }}
                  fullWidth
                  sx={{
                    mt: 1, py: '0.5rem', fontFamily, fontSize: '0.75rem', fontWeight: 500, textTransform: 'none',
                    color: colors.textTertiary,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  Back to Sign In
                </Button>
              </Box>
            )}

            {/* Login Form - Hidden when verification required */}
            {!showVerificationRequired && (
              <>
                {/* Social Login Buttons - side by side */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                  <Button
                    onClick={() => googleLogin()}
                    fullWidth variant="outlined" disabled={anyLoading}
                    startIcon={socialLoading === 'google' ? <CircularProgress size={16} color="inherit" /> : <GoogleIcon />}
                    sx={socialButtonSx}
                  >
                    {socialLoading === 'google' ? 'Connecting...' : 'Google'}
                  </Button>
                  <LinkedIn
                    clientId={linkedin_client_id}
                    redirectUri={linkedin_redirect_uri}
                    onSuccess={handleLinkedInLoginSuccess}
                    onError={handleLinkedInLoginFailure}
                    scope="openid profile email"
                  >
                    {({ linkedInLogin }) => (
                      <Button
                        onClick={linkedInLogin}
                        fullWidth variant="outlined" disabled={anyLoading}
                        startIcon={socialLoading === 'linkedin' ? <CircularProgress size={16} color="inherit" /> : <LinkedInIcon />}
                        sx={socialButtonSx}
                      >
                        {socialLoading === 'linkedin' ? 'Connecting...' : 'LinkedIn'}
                      </Button>
                    )}
                  </LinkedIn>
                </Box>

                <Divider sx={{ my: 1.5, fontSize: '0.6875rem', fontWeight: 600, color: colors.textTertiary, fontFamily, textTransform: 'uppercase', letterSpacing: '0.05em', '&::before, &::after': { borderColor: 'rgba(0,0,0,0.06)' } }}>or continue with email</Divider>

                <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  fullWidth size="small" type="email" placeholder="Email address"
                  value={email} onChange={(e) => setEmail(e.target.value)} required disabled={anyLoading}
                  InputProps={{ startAdornment: <EmailOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
                  sx={inputSx}
                />
                <TextField
                  fullWidth size="small" type="password" placeholder="Password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required disabled={anyLoading}
                  InputProps={{ startAdornment: <LockOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} /> }}
                  sx={inputSx}
                />
              </Box>

              <Box sx={{ textAlign: 'right', mt: 0.75, mb: 1.5 }}>
                <MuiLink href="/forgot-password" underline="hover" sx={{ fontSize: '0.75rem', fontWeight: 500, color: colors.textTertiary, fontFamily, '&:hover': { color: theme.palette.primary.main } }}>
                  Forgot password?
                </MuiLink>
              </Box>

              <Button
                type="submit" fullWidth disabled={anyLoading}
                sx={{
                  py: '0.75rem', fontFamily, fontSize: '0.9375rem', fontWeight: 600, textTransform: 'none',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  color: '#fff', borderRadius: '14px', height: 46, gap: 1,
                  boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': { opacity: 0.7 },
                }}
              >
                {isLoading && <CircularProgress size={18} sx={{ color: '#fff' }} />}
                {isLoading ? "Signing In..." : "Sign In"}
              </Button>
                </form>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mt: 2 }}>
                  <Box sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    fontSize: '0.6875rem', color: colors.textTertiary, fontFamily,
                  }}>
                    <ShieldOutlinedIcon sx={{ fontSize: 13, color: colors.success }} />
                    Secure
                  </Box>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textTertiary }} />
                  <Typography sx={{ fontSize: '0.8125rem', color: colors.textTertiary, fontFamily }}>
                    No account?{' '}
                    <MuiLink href="/SignUp" underline="hover" sx={{ color: theme.palette.primary.main, fontWeight: 600, fontFamily }}>Sign up</MuiLink>
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Box>

    </>
  );
}

export default function UserLogin() {
  return (
    <GoogleOAuthProvider clientId={google_login_api}>
      <SignInInner />
    </GoogleOAuthProvider>
  );
}
